import * as THREE from "three";
import { createRenderer } from "./createRenderer.js";
import { ScrollController } from "../controls/ScrollController.js";
import { PointerController } from "../controls/PointerController.js";
import { AmbientGlowBackground } from "../effects/AmbientGlowBackground.js";
import { lerp } from "../../utils/math.js";
import { THEME_EVENT, getTheme } from "../../config/theme.js";

export class Experience {
  constructor(canvas) {
    this.clock = new THREE.Clock();
    this.renderer = createRenderer(canvas);
    this.scroll = new ScrollController();
    this.pointer = new PointerController();

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x050509);

    this.camera = new THREE.PerspectiveCamera(
      55,
      window.innerWidth / window.innerHeight,
      0.1,
      100
    );
    this.camera.position.set(0, 0, 11);

    this.#setupLights();
    this.background = new AmbientGlowBackground(this.scene);
    window.addEventListener(THEME_EVENT, () => this.#applySceneTheme());
    this.#applySceneTheme();
  }

  #readThreeBg() {
    const themeId = document.documentElement.dataset.theme;
    const fromTheme = getTheme(themeId).three?.background;
    if (typeof fromTheme === "number") return fromTheme;

    const value = getComputedStyle(document.documentElement)
      .getPropertyValue("--three-bg")
      .trim();
    return value || 0x050509;
  }

  #applySceneTheme() {
    try {
      this.scene.background = new THREE.Color(this.#readThreeBg());
    } catch (error) {
      console.error("Could not apply scene background", error);
      this.scene.background = new THREE.Color(0x050509);
    }
  }

  registerScrollSections(sections) {
    for (const [id, element] of Object.entries(sections)) {
      this.scroll.registerSection(id, element);
    }
  }

  #setupLights() {
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.25));

    const key = new THREE.DirectionalLight(0xffffff, 0.6);
    key.position.set(3, 5, 8);
    this.scene.add(key);
  }

  #updateCamera(elapsed, pointer) {
    const drift = Math.sin(elapsed * 0.12) * 0.15;

    this.camera.position.x = lerp(0, pointer.x * 1.1, 0.5) + drift;
    this.camera.position.y = lerp(0, pointer.y * 0.55, 0.5);
    this.camera.position.z = 11 + Math.sin(elapsed * 0.08) * 0.3;
    this.camera.lookAt(pointer.x * 0.2, pointer.y * 0.15, -2);
  }

  update() {
    const elapsed = this.clock.getElapsedTime();
    const pointer = this.pointer.update();

    this.background.update(elapsed, pointer);
    this.#updateCamera(elapsed, pointer);

    this.renderer.render(this.scene, this.camera);
  }

  resize() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }
}
